import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Card,
  CardContent,
  Stack,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SupportIcon from '@mui/icons-material/Support';
import PersonIcon from '@mui/icons-material/Person';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';

const Settings = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated, logout, user } = useBookMyOpAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // DRY: Reusable button styling function
  const getButtonStyles = (color = theme.palette.primary.main) => ({
    borderRadius: '16px',
    textTransform: 'none',
    py: 2,
    px: 3,
    fontSize: '1.1rem',
    fontWeight: 600,
    borderColor: color,
    color: color,
    borderWidth: '2px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      borderColor: color === theme.palette.error.main ? theme.palette.error.dark : theme.palette.primary.dark,
      backgroundColor: color === theme.palette.error.main ? theme.palette.error.light + '15' : theme.palette.primary.light + '15',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    }
  });

  const handleLogout = () => {
    logout();
    // Redirect to login page on mobile, home page on desktop
    if (isMobile) {
      navigate('/login');
    } else {
      navigate('/');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ 
            mb: 3, 
            textTransform: 'none',
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            }
          }}
        >
          Back
        </Button>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.5rem' },
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
            mb: 1,
          }}
        >
          Settings
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ textAlign: 'center', fontSize: '1.1rem' }}
        >
          Manage your account and preferences
        </Typography>
      </Box>

      {/* Settings Options */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(0,0,0,0.05)',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              fontWeight: 600,
              color: theme.palette.text.primary,
              textAlign: 'center'
            }}
          >
            Account Settings
          </Typography>
          
          <Stack spacing={3}>
            <Button
              variant="outlined"
              startIcon={<SupportIcon />}
              onClick={() => navigate('/support')}
              fullWidth
              sx={getButtonStyles()}
            >
              Contact Support
            </Button>

            {isAuthenticated && (
              <Button
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={() => navigate('/profile')}
                fullWidth
                sx={getButtonStyles()}
              >
                Profile
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>


      {/* Account Actions - Only show if authenticated */}
      {isAuthenticated && (
        <Card sx={{ 
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0,0,0,0.05)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                color: theme.palette.text.primary,
                textAlign: 'center'
              }}
            >
              Account Information
            </Typography>
            
            {user && (user.name || user.mobile_number) && (
              <Box sx={{ 
                mb: 4, 
                p: 3, 
                bgcolor: theme.palette.primary.light + '10', 
                borderRadius: '16px',
                border: `2px solid ${theme.palette.primary.light}30`,
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                  Logged in as:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {user.name || user.mobile_number}
                </Typography>
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={<DeleteForeverIcon />}
              onClick={() => navigate('/delete-account')}
              fullWidth
              sx={getButtonStyles(theme.palette.text.secondary)}
            >
              Delete my account
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              fullWidth
              sx={getButtonStyles(theme.palette.error.main)}
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Not authenticated message */}
      {!isAuthenticated && (
        <Card sx={{ 
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(0,0,0,0.05)',
        }}>
          <CardContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Sign in to access more settings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Login to your account to manage your preferences and account settings.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                borderRadius: '16px',
                textTransform: 'none',
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                }
              }}
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Settings; 